import { ApiError } from "@allergy123/api-client";
import type { BillingPlan, SupplyOrderStatus } from "@allergy123/contracts";
import { errorText } from "../catalog/common";

/** How each plan reads to staff (design 2026-10-25). */
export const BILLING_PLAN_LABELS: Record<BillingPlan, string> = {
  alc: "A la Carte",
  bas: "Base",
  bas_m2m: "Base (month to month)",
  cor: "Core",
  cus: "Custom",
};

export const ORDER_STATUS_LABELS: Record<SupplyOrderStatus, string> = {
  received: "Received",
  confirmed: "Confirmed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** Cents as the amount staff read: `1250` → `$12.50`. The API never speaks in dollars. */
export const dollars = (cents: number): string => usd.format(cents / 100);

/**
 * Dollars staff typed into a field, as cents for the API; null when the text is not a non-negative amount.
 * Rounded to the cent, so `0.1 + 0.2` style float noise never reaches the request.
 */
export function centsFromDollars(text: string): number | null {
  const t = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/** An ISO timestamp as a short local date. */
export const shortDate = (iso: string): string => new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(iso));

/** Today as the `YYYY-MM-DD` the account contract takes, in the staff member's own calendar. */
export function todayCalendarDate(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const MESSAGES: Record<string, string> = {
  no_billing_account: "This organization has no billing account yet. Save the account first.",
  no_stripe_customer: "No Stripe customer is linked. Link an existing customer or create one first.",
  no_payment_method: "The practice has no payment method on file. Send a setup link and wait for it to be used.",
  plan_has_no_subscription: "Only the Base and Core plans have a Program Fee subscription. A la Carte is charged per test; a Custom plan is billed by hand.",
  subscription_exists: "A Program Fee subscription is already running for this organization.",
  no_subscription: "There is no running Program Fee subscription to stop.",
  no_active_locations: "The organization has no active locations, so there is nothing to subscribe. Add a location first.",
  price_not_configured: "The price for this plan or product is not set up in Stripe yet. Add a price with the SKU as its lookup key.",
  invalid_transition: "The order has moved on since the page loaded, so this step no longer applies. The list has been reloaded.",
  version_conflict: "Someone else changed this since the page loaded. The latest version has been reloaded; check it and try again.",
  billing_unavailable: "Billing is not available on this API. Start the core API with npm run api:dev, or check its Stripe configuration.",
  unknown_stripe_customer: "Stripe has no customer with that id. Check the id (it starts with cus_) and try again.",
  billing_email_required: "A billing email is needed to create the Stripe customer.",
  setup_link_unconfigured: "The setup link's return address is not configured on the API, so no link can be created.",
  account_not_active: "The billing account is not active.",
  custom_plan_order_by_staff: "A Custom plan's orders are placed by staff, not online.",
  not_found: "Not found — it may have been removed.",
};

/** A refusal as one plain sentence; falls back to the console's shared error text for codes shared with the rest of the console. */
export function billingErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const base = MESSAGES[err.code];
    if (base) {
      const lookupKey = err.details.lookupKey;
      return err.code === "price_not_configured" && typeof lookupKey === "string" ? `${base} Missing: ${lookupKey}.` : base;
    }
  }
  return errorText(err);
}

/** True for the two refusals that mean "what you see is stale": the page should reload before the next try. */
export const isStale = (err: unknown): boolean => err instanceof ApiError && (err.code === "version_conflict" || err.code === "invalid_transition");
